import { is } from 'bpmn-js/lib/util/ModelUtil';
import { useService } from 'bpmn-js-properties-panel';
import { html } from 'htm/preact';
import { useState } from '@bpmn-io/properties-panel/preact/hooks';
import PropTypes from 'prop-types';
import { isSafeImageUrl } from '../utils/sanitize';
import { getOpenLightbox } from '../utils/bridge';
import { optimizeImage, validateFileSize } from '../utils/imageProcessing';

/**
 * Image group — shown only for Task elements (all subtypes).
 * Provides a URL input field, thumbnail preview, and zoomable lightbox.
 */
export default function ImageGroup(element, injector) {
  const translate = injector.get('translate');

  if (!is(element, 'bpmn:Task')) {
    return null;
  }

  return {
    id: 'image',
    label: translate('Bild'),
    entries: [
      {
        id: 'imageUrl',
        component: ImageUploadEntry
      },
      {
        id: 'imagePreview',
        component: ImagePreview
      }
    ]
  };
}

// --- File Upload Entry ---
function ImageUploadEntry(props) {
  const { element } = props;

  const modeling = useService('modeling');
  const translate = useService('translate');
  const [isProcessing, setIsProcessing] = useState(false);

  const hasImage = () => {
    const bo = element.businessObject;
    return !!bo?.get?.('custom:imageUrl');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      alert(translate('Nur PNG- und JPG-Dateien sind erlaubt.'));
      e.target.value = '';
      return;
    }

    // Validate file size before processing
    const sizeCheck = validateFileSize(file);
    if (!sizeCheck.ok) {
      alert(sizeCheck.message);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      setIsProcessing(true);
      try {
        const rawDataUrl = event.target.result;
        // Resize & compress (max 1920×1080, JPEG 0.82 quality)
        const optimized = await optimizeImage(rawDataUrl);
        modeling.updateModdleProperties(element, element.businessObject, {
          'custom:imageUrl': optimized
        });
      } catch (err) {
        console.error('Image optimization failed:', err);
        alert(translate('Fehler bei der Bildverarbeitung.'));
      } finally {
        setIsProcessing(false);
      }
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleRemove = () => {
    modeling.updateModdleProperties(element, element.businessObject, {
      'custom:imageUrl': undefined
    });
  };

  return html`
    <div class="bio-properties-panel-entry" style="padding: 6px 15px 10px;">
      <label class="bio-properties-panel-label">${translate('Screenshot (PNG/JPG)')}</label>
      <div class="d-flex gap-1 mt-1">
        <label class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 mb-0"
               style=${isProcessing ? 'pointer-events:none;opacity:.6' : ''}>
          <i class="bi ${isProcessing ? 'bi-hourglass-split' : 'bi-upload'}"></i>
          ${isProcessing ? translate('Wird optimiert…') : translate('Hochladen')}
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange=${handleFileChange}
            style="display:none"
            disabled=${isProcessing}
          />
        </label>
        ${!isProcessing && hasImage() && html`
          <button
            type="button"
            class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
            onClick=${handleRemove}
            title=${translate('Bild entfernen')}
          >
            <i class="bi bi-trash"></i> ${translate('Entfernen')}
          </button>
        `}
      </div>
    </div>
  `;
}

// --- Open lightbox via React (module bridge) ---
export function openLightbox(imageUrl, element) {
  const fn = getOpenLightbox();
  if (fn) fn(imageUrl, element);
}

// --- Thumbnail Preview (no hooks needed) ---
function ImagePreview(props) {
  const { element } = props;
  const bo = element.businessObject;
  const imageUrl = bo?.get?.('custom:imageUrl') ?? null;

  if (!imageUrl || !isSafeImageUrl(imageUrl)) {
    return null;
  }

  return html`
    <div class="image-preview-container">
      <img
        class="image-preview-thumbnail"
        src=${imageUrl}
        alt="Task-Bild"
        onClick=${() => openLightbox(imageUrl, element)}
        onError=${(e) => {
          e.target.style.display = 'none';
          e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'block');
        }}
        title="Klicken zum Vergrößern"
      />
      <div class="image-error" style="display:none">⚠ Bild nicht verfügbar</div>
    </div>
  `;
}

ImagePreview.propTypes = {
  element: PropTypes.shape({
    businessObject: PropTypes.shape({
      get: PropTypes.func,
    }),
  }).isRequired,
};
